import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  TableRow,
  TableCell,
  Table,
  TableBody,
  TablePagination,
  IconButton,
} from "@material-ui/core";
import Chip from "@material-ui/core/Chip";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import { useSelector, useDispatch } from "react-redux";
import "./ExpandedTableRow.scss";
import { dataguard_icons } from "../../../components/common/utilities";
import { RenderUserName } from "../Utility/DetailsPopupConstant";
import { openDetailsPopup } from "../Popup/ActionCreator";
import {
  LOCATION,
  BEHAVIORMODEL,
  ACTIVE_USERS,
  ROLE_NAME,
  ROLES,
  OWNERID,
  DATA_ASSETS,
  PUBLIC_DATA,
  POLICY,
  POLICYID,
  COUNTRY,
  DATASOURCE,
  getHierarcyDataKey,
  ACTIVITIES,
  USERS,
  ANOMALIES,
  ASSETS,
  OPEN_ANOMALIES,
} from "../Popup/ActionTypes";
import {
  formatNumber,
  renderFlag,
  renderCountryNamesWithPopover,
  ACTION_NOT_NEEDED,
  FETCH_CITY_LEVEL_DATA,
} from "../Utility/DataguardConstant";
import { processCountryData, renderPolarChart } from "./config/pageConfigurations";
import SeverityChips from "../DataGuardCommonComponents";
import { renderDonutChart } from "./config/pageConfigurations";
import Page_Loader from "../../../assets/vizr_images/page_loader.gif";

// Palette used for the unique-data-assets per-source pie at every level
// (country, city, datasource), matching the existing parent-level chart
// colors seen in the product (blue, amber, red, teal, orange, ...).
const ASSET_SOURCE_COLORS = [
  "#5C7CFA",
  "#F2A33C",
  "#E0524D",
  "#4FC3D9",
  "#F08A3C",
  "#7C6FE0",
  "#4CAF50",
  "#D14FA0",
];

function colorForIndex(idx) {
  return ASSET_SOURCE_COLORS[idx % ASSET_SOURCE_COLORS.length];
}


function buildAssetSourceSlices(perDsArray, dsMetaLookup) {
  return (perDsArray || [])
    .filter((item) => (item.dataAssetsCount || item.count || 0) > 0)
    .map((item, idx) => {
      const meta = dsMetaLookup ? dsMetaLookup[item.dataSourceId] : null;
      const name = item.dataSourceName || meta?.dataSourceName || "Unknown";
      const type = item.dataSourceType || meta?.dataSourceType || "";
      return {
        count: item.dataAssetsCount ?? item.count ?? 0,
        name: type ? `${name} (${type})` : name,
        color: colorForIndex(idx),
      };
    });
}

function buildCityDataSourceRows(cityObj) {
  if (!cityObj) return [];

  const dsMap = {};

  const ensureDs = (dsId, meta) => {
    if (!dsMap[dsId]) {
      dsMap[dsId] = {
        dataSourceId: dsId,
        dataSourceName: meta?.dataSourceName || "-",
        dataSourceType: meta?.dataSourceType || "-",
        usersCount: 0,
        anomalyCount: 0,
        activitiesCount: 0,
        dataAssetsCount: 0,
      };
    }
  };

  (cityObj.usersCountPerDs || []).forEach((item) => {
    ensureDs(item.dataSourceId, item);
    dsMap[item.dataSourceId].usersCount = item.usersCount || 0;
  });

  (cityObj.noOfAnomaliesPerDs || []).forEach((item) => {
    ensureDs(item.dataSourceId, item);
    dsMap[item.dataSourceId].anomalyCount = item.anomalyCount || 0;
    dsMap[item.dataSourceId].openCloseAnomaliesCount = {
      open: item.Unresolved || 0,
      close: (item.anomalyCount || 0) - (item.Unresolved || 0),
    };
  });

  (cityObj.noOfActivitiesPerDs || []).forEach((item) => {
    ensureDs(item.dataSourceId, item);
    dsMap[item.dataSourceId].activitiesCount = item.activitiesCount || 0;
  });

  (cityObj.uniqueDataAssetsPerDs || []).forEach((item) => {
    ensureDs(item.dataSourceId, item);
    dsMap[item.dataSourceId].dataAssetsCount = item.dataAssetsCount || 0;
  });

  return Object.values(dsMap);
}

function CityDataSourceRows({
  cityObj,
  row,
  columns,
  onCellClick,
  renderAnomalies,
}) {
  const dsRows = useMemo(() => buildCityDataSourceRows(cityObj), [cityObj]);

  const activeColumns = [
    { key: "usersCount", id: "usersAccumulated" },
    { key: "anomalyCount", id: "anomalyAccumulated" },
    { key: "activitiesCount", id: "activitiesAccumulated" },
    { key: "dataAssetsCount", id: "uniqueDataAssetsAccumulated" },
  ];

  if (dsRows.length === 0) {
    return (
      <TableRow className="datasource-row datasource-row--empty">
        <TableCell
          colSpan={columns.length + 1}
          style={{
            paddingLeft: 80,
            fontStyle: "italic",
            color: "rgba(0,0,0,0.45)",
            fontSize: 12.5,
          }}
        >
          No data source breakdown available for this city.
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {dsRows.map((item) => (
        <TableRow
          key={item.dataSourceId}
          className="clickable-table-row child-row child-row-visible datasource-row"
        >
          <TableCell
            align="left"
            style={{ width: columns[0]?.childWidth, paddingLeft: 80 }}
          >
            <div
              className="data-guard-accordion-type first-col-sub-accord"
              style={{ marginLeft: 0 }}
            >
              <img
                className="ds-icon"
                src={dataguard_icons[item.dataSourceType]}
                alt={item.dataSourceType}
              />
              <div>{item.dataSourceName}</div>
            </div>
          </TableCell>

          {columns.slice(1).map((column) => {
            const activeCol = activeColumns.find((col) => col.id === column.id);

            if (!activeCol) {
              return (
                <TableCell
                  key={column.id}
                  style={{ width: column.childWidth, ...column.customStyles }}
                />
              );
            }

            if (activeCol.id === "uniqueDataAssetsAccumulated") {
              const val = formatNumber(item.dataAssetsCount);
              return (
                <TableCell
                  key={column.id}
                  align="center"
                  style={{ width: column.childWidth, ...column.customStyles }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCellClick(e, row, activeCol, item);
                  }}
                >
                  <Chip
                    size="medium"
                    label={val}
                    color="primary"
                    className="user-chip numberValues"
                  />
                </TableCell>
              );
            }

            const val =
              typeof item[activeCol.key] === "object"
                ? formatNumber(item[activeCol.key]?.count)
                : formatNumber(item[activeCol.key]);

            let extraClass = "";
            if (["anomalyCount"].includes(activeCol.key)) {
              extraClass = val === "0" || val === 0 ? "success" : "error";
            }

            return (
              <TableCell
                key={column.id}
                align="center"
                style={{ width: column.childWidth, ...column.customStyles }}
                onClick={(e) => {
                  e.stopPropagation();
                  onCellClick(e, row, activeCol, item);
                }}
              >
                {renderAnomalies(val, extraClass, activeCol, item)}
              </TableCell>
            );
          })}

          {!ACTION_NOT_NEEDED.includes("locations") && (
            <TableCell padding="checkbox" />
          )}
        </TableRow>
      ))}
    </>
  );
}

function CityLevelGrid({ row, columns, onCellClick, renderAnomalies }) {
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [expandedCityIds, setExpandedCityIds] = useState([]);

  const country = row?.location;

  const cityLevelDataForCountry = useSelector(
    (state) => state.DataguardReducer.cityLevelDataByCountry?.[country],
  );
  const cityLevelLoader = useSelector((state) => state.DataguardReducer.cityLevelLoader);

  const cityLevelData = cityLevelDataForCountry?.content || [];

  const {
    totalElements = 0,
    totalPages = 0,
    size = 10,
    number = 0,
  } = cityLevelDataForCountry || {};
  

  useEffect(() => {
    dispatch({
      type: FETCH_CITY_LEVEL_DATA,
      payload: { country, page, size: pageSize },
    });
  }, [dispatch, country, page, pageSize]);

  const allCityRows = useMemo(
    () =>
      (cityLevelData || []).map((cityObj, idx) => ({
        id: `${country}-${cityObj.city || idx}`,
        city: cityObj.city,
        usersCount: (cityObj.usersCountPerDs || []).reduce(
          (sum, item) => sum + (item.usersCount || 0),
          0,
        ),
        anomalyCount: (cityObj.noOfAnomaliesPerDs || []).reduce(
          (sum, item) => sum + (item.anomalyCount || 0),
          0,
        ),
        activitiesCount: (cityObj.noOfActivitiesPerDs || []).reduce(
          (sum, item) => sum + (item.activitiesCount || 0),
          0,
        ),
        dataAssetsCount: (cityObj.uniqueDataAssetsPerDs || []).reduce(
          (sum, item) => sum + (item.dataAssetsCount || 0),
          0,
        ),
        assetSourceSlices: buildAssetSourceSlices(cityObj.uniqueDataAssetsPerDs),
        openCloseAnomaliesCount: (cityObj.noOfAnomaliesPerDs || []).reduce(
          (acc, item) => ({
            open: acc.open + (item.Unresolved || 0),
            close:
              acc.close + ((item.anomalyCount || 0) - (item.Unresolved || 0)),
          }),
          { open: 0, close: 0 },
        ),
        _raw: cityObj,
      })),
    [cityLevelData, country],
  );

  const cityRows = allCityRows;

  const toggleCityExpand = useCallback((cityId) => {
    setExpandedCityIds((prev) =>
      prev.includes(cityId)
        ? prev.filter((id) => id !== cityId)
        : [...prev, cityId],
    );
  }, []);

  const cityActiveColumns = [
    { key: "usersCount", id: "usersAccumulated" },
    { key: "anomalyCount", id: "anomalyAccumulated" },
    { key: "activitiesCount", id: "activitiesAccumulated" },
    { key: "dataAssetsCount", id: "uniqueDataAssetsAccumulated" },
  ];

  return (
    <div className="city-level-grid-wrap">
      {cityLevelLoader && (
        <img
          src={Page_Loader}
          alt="loading"
          className="loader-data-source dataTagPopupLoader"
        />
      )}

      <Table size="small" className="city-level-table">
        <TableBody>
          {cityRows.map((cityRow) => {
            const isCityOpen = expandedCityIds.includes(cityRow.id);
            return (
              <React.Fragment key={cityRow.id}>
                <TableRow
                  className="clickable-table-row city-row"
                  onClick={() => toggleCityExpand(cityRow.id)}
                >
                  <TableCell
                    align="left"
                    style={{
                      width: columns[0]?.childWidth,
                      paddingLeft: 17,
                      boxSizing: "border-box",
                    }}
                  >
                    <div className="data-guard-accordion-type">
                      <div>{cityRow.city}</div>
                    </div>
                  </TableCell>

                  {columns.slice(1).map((column) => {
                    const activeCol = cityActiveColumns.find(
                      (col) => col.id === column.id,
                    );
                    if (!activeCol) {
                      return (
                        <TableCell
                          key={column.id}
                          style={{
                            width: column.childWidth,
                            ...column.customStyles,
                          }}
                        />
                      );
                    }

                    const val = formatNumber(cityRow[activeCol.key]);
                    let extraClass = "";
                    if (activeCol.key === "anomalyCount") {
                      extraClass =
                        val === "0" || val === 0 ? "success" : "error";
                    }

                    return (
                      <TableCell
                        key={column.id}
                        align="center"
                        style={{
                          width: column.childWidth,
                          ...column.customStyles,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onCellClick(e, row, activeCol, cityRow._raw);
                        }}
                      >
                        {renderAnomalies(val, extraClass, activeCol, cityRow)}
                      </TableCell>
                    );
                  })}

                  {/* Expand toggle -- last column, mirroring the parent table's toggle column */}
                  {!ACTION_NOT_NEEDED.includes("locations") && (
                    <TableCell padding="checkbox">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCityExpand(cityRow.id);
                        }}
                        style={{
                          background: "#e5e9ff",
                          borderRadius: 5,
                          color: "#535458",
                        }}
                      >
                        {isCityOpen ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>

                {/* Datasource breakdown renders immediately below this
                    city's own row, not after the whole list/footer */}
                {isCityOpen && (
                  <CityDataSourceRows
                    cityObj={cityRow._raw}
                    row={row}
                    columns={columns}
                    onCellClick={onCellClick}
                    renderAnomalies={renderAnomalies}
                  />
                )}
              </React.Fragment>
            );
          })}

          {cityRows.length === 0 && !cityLevelLoader && (
            <TableRow>
              <TableCell
                colSpan={columns.length + 1}
                style={{
                  textAlign: "center",
                  padding: 16,
                  color: "rgba(0,0,0,0.45)",
                  fontSize: 13,
                }}
              >
                No cities found for this location.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        rowsPerPageOptions={[10, 25, 50]}
        count={totalElements}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={(_, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(e) => {
          setPageSize(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </div>
  );
}

const ExpandableTableRow = ({ row, columns, selectedCategory }) => {
  const [open, setOpen] = useState(false);
  const isIncognito = useSelector((state) => state.DataguardReducer.incognito);

  // Extract widths from columns configuration and prepare activeColumns for expanded table
  const firstColumnWidth = columns[0]?.width;
  const dispatch = useDispatch();
  const openPopup = (props, isChildLevel = false) => {
    dispatch(openDetailsPopup(props, isChildLevel));
  };

  const isLocationsCategory = selectedCategory === "locations";

  // Define which fields to show per category, with widths from pageConfigurations
  const categoryColumns = {
    locations: [
      {
        key: "usersCount",
        label: "Users",
        id: "usersAccumulated",
        width: columns.find((col) => col.id === "usersAccumulated")?.width,
      },
      {
        key: "anomalyCount",
        chartType: "pie",
        label: "Anomaly",
        id: "anomalyAccumulated",
        width: columns.find((col) => col.id === "anomalyAccumulated")?.width,
      },
      {
        key: "activitiesCount",
        label: "Activities",
        id: "activitiesAccumulated",
        width: columns.find((col) => col.id === "activitiesAccumulated")?.width,
      },
      {
        key: "dataAssetsCount",
        label: "Data Assets",
        id: "uniqueDataAssetsAccumulated",
        width: columns.find((col) => col.id === "uniqueDataAssetsAccumulated")
          ?.width,
      },
    ],
    roles: [
      {
        key: "usersCount",
        label: "Users",
        id: "users",
        width: columns.find((col) => col.id === "users")?.width,
      },
      {
        key: "activeUsersCount",
        label: "Active Users",
        id: "activeUsersAccumulated",
        width: columns.find((col) => col.id === "activeUsersAccumulated")
          ?.width,
      },
      {
        key: "dummy1",
        isdummy: true,
        label: "",
        id: "percentageOfTotalUsers",
        width: columns.find((col) => col.id === "percentageOfTotalUsers")
          ?.width,
      },
      {
        key: "anomaliesCount",
        label: "Anomalies",
        chartType: "pie",
        id: "anomaliesAccumulated",
        width: columns.find((col) => col.id === "anomaliesAccumulated")?.width,
      },
      {
        key: "locations",
        label: "Locations",
        chartType: "map",
        id: "locations",
        width: columns.find((col) => col.id === "locations")?.width,
      },
    ],
    publicdata: [
      {
        key: "assetsCount",
        label: "Public Assets",
        id: "assetsAccumulated",
        width: columns.find((col) => col.id === "assetsAccumulated")?.width,
      },
      {
        key: "activitiesCount",
        label: "Activities",
        id: "activitiesAccumulated",
        width: columns.find((col) => col.id === "activitiesAccumulated")?.width,
      },
      {
        key: "dummy1",
        isdummy: true,
        label: "",
        id: "locations",
        width: columns.find((col) => col.id === "locations")?.width,
      },
      {
        key: "dummy2",
        isdummy: true,
        label: "",
        id: "action",
        width: columns.find((col) => col.id === "action")?.width,
      },
    ],
    behaviormodels: [
      {
        key: "usersCount",
        label: "Users",
        id: "users",
        width: columns.find((col) => col.id === "users")?.width,
      },
      {
        key: "totalAnomaliesCount",
        chartType: "pie",
        label: "Total Anomalies",
        id: "totalAnomaliesByDs",
        width: columns.find((col) => col.id === "totalAnomaliesByDs")?.width,
      },
      {
        key: "severity",
        label: "Severity",
        id: "severity",
        width: columns.find((col) => col.id === "severity")?.width,
      },
      {
        key: "dummy2",
        isdummy: true,
        label: "",
        id: "action",
        width: columns.find((col) => col.id === "action")?.width,
      },
    ],
  };

  const activeColumns = categoryColumns[selectedCategory] || [];
  const getOpenCloseAnomaliesCount = (row, dsId) => {
    const anomaliesList =
      selectedCategory === "roles" ? row.anomalies : row.anomaly;
    const selectedDS = anomaliesList.find((an) => an.dataSourceId === dsId);
    return {
      open: selectedDS?.Unresolved || 0,
      close: selectedDS?.totalAnomaliesCount - selectedDS?.Unresolved || 0,
    };
  };
  const getMapData = (row, dsId) => {
    return [];
  };

  // Build per data source row from accumulated values (non-locations only)
  const perDsData = useMemo(() => {
    if (isLocationsCategory) return [];

    const dsMap = {};
    let allDsIds = new Set();

    switch (selectedCategory) {
      case "behaviormodels":
        allDsIds = new Set([
          ...Object.keys(row.users || {}),
          ...Object.keys(row.totalAnomaliesByDs || {}),
          ...Object.keys(row.openAnomalies || {}),
        ]);
        break;
      case "roles":
        allDsIds = new Set([
          ...Object.keys(row.usersAccumulated || {}),
          ...Object.keys(row.anomaliesAccumulated || {}),
          ...Object.keys(row.activeUsersAccumulated || {}),
        ]);
        break;
      case "publicdata":
        allDsIds = new Set([
          ...Object.keys(row.assetsAccumulated || {}),
          ...Object.keys(row.activitiesAccumulated || {}),
        ]);
        break;
      default:
        allDsIds = new Set();
    }

    allDsIds.forEach((dsId) => {
      const dsMeta =
        row.users?.[dsId] ||
        row.totalAnomaliesByDs?.[dsId] ||
        row.openAnomalies?.[dsId] ||
        row.usersAccumulated?.[dsId] ||
        row.anomaliesAccumulated?.[dsId] ||
        row.activeUsersAccumulated?.[dsId] ||
        row.activitiesAccumulated?.[dsId] ||
        row.assetsAccumulated?.[dsId] ||
        row.publicAssetAccessCountPerDs?.[dsId] ||
        {};

      if (selectedCategory === "roles") {
        dsMap[dsId] = {
          dataSourceId: dsId,
          dataSourceName: dsMeta.dataSourceName || "-",
          dataSourceType: dsMeta.dataSourceType || "-",
          usersCount: row.usersAccumulated?.[dsId] ?? "-",
          anomaliesCount: row.anomaliesAccumulated?.[dsId] ?? "-",
          activeUsersCount: row.activeUsersAccumulated?.[dsId] ?? "-",
          openCloseAnomaliesCount: getOpenCloseAnomaliesCount(row, dsId),
          countries: (row.countries || []).filter(
            (country) => country.dataSourceId === dsId,
          ),
        };
      } else if (selectedCategory === "publicdata") {
        dsMap[dsId] = {
          dataSourceId: dsId,
          dataSourceName: dsMeta.dataSourceName || "-",
          dataSourceType: dsMeta.dataSourceType || "-",
          assetsCount: row.assetsAccumulated?.[dsId] ?? "-",
          activitiesCount: row.activitiesAccumulated?.[dsId] ?? "-",
        };
      } else if (selectedCategory === "behaviormodels") {
        dsMap[dsId] = {
          dataSourceId: dsId,
          dataSourceName: dsMeta.dataSourceName || "-",
          dataSourceType: dsMeta.dataSourceType || "-",
          usersCount: row.users?.[dsId]?.count ?? 0,
          totalAnomaliesCount: row.totalAnomaliesByDs?.[dsId]?.count ?? 0,
          openAnomaliesCount: row.openAnomalies?.[dsId]?.count ?? 0,
          highCount: row.totalAnomaliesByDs?.[dsId]?.high ?? 0,
          mediumCount: row.totalAnomaliesByDs?.[dsId]?.medium ?? 0,
          lowCount: row.totalAnomaliesByDs?.[dsId]?.low ?? 0,
        };
      }
    });

    return Object.values(dsMap);
  }, [row, selectedCategory, isLocationsCategory]);

  const childRows = perDsData;

  const onCellClick = (e, row, col, item) => {
    const detailsPageName = selectedCategory;
    const dataSourceIds = item
      ? item.dataSourceId
      : perDsData.map((ds) => ds.dataSourceId).join(",") || "";
    const isChildLevel = !!item?.dataSourceId;
    const dataSourceDetails = isChildLevel
      ? {
          dataSourceName: item.dataSourceName,
          dataSourceType: item.dataSourceType,
        }
      : "";
      const cityDetails =
      detailsPageName.toLowerCase() === "locations" &&
      item?.city
        ? { city: item.city }
        : {};
    switch (detailsPageName.toLowerCase()) {
      case "locations":
        if (col.id === "activitiesAccumulated") {
          const props = {
            hierarchy: [LOCATION, COUNTRY, DATASOURCE, ACTIVITIES],
            subtitleDisplayName: row.location,
            [getHierarcyDataKey(COUNTRY)]: row.location,
            // [getHierarcyDataKey(DATASOURCE)]: dataSourceIds || "",
            ...(dataSourceDetails && { dataSourceDetails }),
            ...cityDetails,
          };
          openPopup(props, isChildLevel);
        } else if (col.id === "usersAccumulated") {
          const props = {
            hierarchy: [LOCATION, COUNTRY, DATASOURCE, USERS],
            subtitleDisplayName: row.location,
            [getHierarcyDataKey(COUNTRY)]: row.location,
            [getHierarcyDataKey(DATASOURCE)]: dataSourceIds,
            ...(dataSourceDetails && { dataSourceDetails }),
            ...cityDetails,
          };
          openPopup(props, isChildLevel);
        } else if (col.id === "anomalyAccumulated") {
          const props = {
            hierarchy: [LOCATION, COUNTRY, DATASOURCE, ANOMALIES],
            subtitleDisplayName: row.location,
            [getHierarcyDataKey(COUNTRY)]: row.location,
            [getHierarcyDataKey(DATASOURCE)]: dataSourceIds,
            ...(dataSourceDetails && { dataSourceDetails }),
            ...cityDetails,
          };
          openPopup(props, isChildLevel);
        } else if (col.id === "uniqueDataAssetsAccumulated") {
          const props = {
            hierarchy: [LOCATION, COUNTRY, DATASOURCE, ASSETS],
            subtitleDisplayName: row.location,
            [getHierarcyDataKey(COUNTRY)]: row.location,
            [getHierarcyDataKey(DATASOURCE)]: dataSourceIds,
            ...(dataSourceDetails && { dataSourceDetails }),
            ...cityDetails,
          };
          openPopup(props, isChildLevel);
        }
        break;
      case "behaviormodels":
        if (col.id === "users") {
          const props = {
            hierarchy: [BEHAVIORMODEL, POLICY, DATASOURCE, USERS],
            subtitleDisplayName: row.policyName,
            [getHierarcyDataKey(POLICYID)]: row.policyId,
            [getHierarcyDataKey(DATASOURCE)]: dataSourceIds,
            ...(dataSourceDetails && { dataSourceDetails }),
          };
          openPopup(props, isChildLevel);
        } else if (col.id === "totalAnomaliesByDs") {
          const props = {
            hierarchy: [BEHAVIORMODEL, POLICY, DATASOURCE, ANOMALIES],
            subtitleDisplayName: row.policyName,
            [getHierarcyDataKey(POLICYID)]: row.policyId,
            [getHierarcyDataKey(DATASOURCE)]: dataSourceIds,
            ...(dataSourceDetails && { dataSourceDetails }),
          };
          openPopup(props, isChildLevel);
        } else if (col.id === "severity") {
          const props = {
            hierarchy: [BEHAVIORMODEL, POLICY, DATASOURCE, OPEN_ANOMALIES],
            subtitleDisplayName: row.policyName,
            [getHierarcyDataKey(POLICYID)]: row.policyId,
            [getHierarcyDataKey(DATASOURCE)]: dataSourceIds,
            ...(dataSourceDetails && { dataSourceDetails }),
          };
          openPopup(props, isChildLevel);
        }
        break;
      case "publicdata":
        if (col.id === "assetsAccumulated") {
          const props = {
            hierarchy: [PUBLIC_DATA, ASSETS, DATASOURCE, DATA_ASSETS],
            subtitleDisplayName: RenderUserName(
              { value: row.ownerName },
              isIncognito,
            ),
            [getHierarcyDataKey(OWNERID)]: row.ownerId,
            [getHierarcyDataKey(DATASOURCE)]: dataSourceIds,
            ...(dataSourceDetails && { dataSourceDetails }),
          };
          openPopup(props, isChildLevel);
        } else if (col.id === "activitiesAccumulated") {
          const props = {
            hierarchy: [PUBLIC_DATA, ASSETS, DATASOURCE, ACTIVITIES],
            subtitleDisplayName: RenderUserName(
              { value: row.ownerName },
              isIncognito,
            ),
            [getHierarcyDataKey(OWNERID)]: row.ownerId,
            [getHierarcyDataKey(DATASOURCE)]: dataSourceIds,
            ...(dataSourceDetails && { dataSourceDetails }),
          };
          openPopup(props, isChildLevel);
        } else if (col.id === "locations") {
          const country = e?.target?.title || "";
          if (!country) return;
          const props = {
            hierarchy: [PUBLIC_DATA, ASSETS, DATASOURCE, COUNTRY],
            [getHierarcyDataKey(OWNERID)]: row.ownerId,
            subtitleDisplayName: RenderUserName(
              { value: row.ownerName },
              isIncognito,
            ),
            [getHierarcyDataKey(DATASOURCE)]: dataSourceIds,
            [getHierarcyDataKey(COUNTRY)]: country,
            ...(dataSourceDetails && { dataSourceDetails }),
          };
          openPopup(props, isChildLevel);
        }
        break;
      case "roles":
        if (col.id === "users" || col.id === "percentageOfTotalUsers") {
          const props = {
            hierarchy: [ROLES, DATASOURCE, USERS],
            subtitleDisplayName: row.roleName,
            [getHierarcyDataKey(ROLE_NAME)]: row.roleName,
            [getHierarcyDataKey(DATASOURCE)]: dataSourceIds,
            ...(dataSourceDetails && { dataSourceDetails }),
          };
          openPopup(props, isChildLevel);
        } else if (col.id === "activeUsersAccumulated") {
          const props = {
            hierarchy: [ROLES, DATASOURCE, ACTIVE_USERS],
            subtitleDisplayName: row.roleName,
            [getHierarcyDataKey(ROLE_NAME)]: row.roleName,
            [getHierarcyDataKey(DATASOURCE)]: dataSourceIds,
            ...(dataSourceDetails && { dataSourceDetails }),
          };
          openPopup(props, isChildLevel);
        } else if (col.id === "anomaliesAccumulated") {
          const props = {
            hierarchy: [ROLES, DATASOURCE, ANOMALIES],
            subtitleDisplayName: row.roleName,
            [getHierarcyDataKey(ROLE_NAME)]: row.roleName,
            [getHierarcyDataKey(DATASOURCE)]: dataSourceIds,
            ...(dataSourceDetails && { dataSourceDetails }),
          };
          openPopup(props, isChildLevel);
        } else if (col.id === "locations") {
          const country = e?.target?.title || e?.target?.alt || "";
          if (!country) return;
          const props = {
            hierarchy: [ROLES, DATASOURCE, COUNTRY],
            subtitleDisplayName: row.roleName,
            [getHierarcyDataKey(ROLE_NAME)]: row.roleName,
            [getHierarcyDataKey(DATASOURCE)]: dataSourceIds,
            [getHierarcyDataKey(COUNTRY)]: country,
            ...(dataSourceDetails && { dataSourceDetails }),
          };
          openPopup(props, isChildLevel);
        }
        break;
      default:
        break;
    }
  };

  const handleRowClick = () => {
    if (isLocationsCategory) {
      setOpen((prev) => !prev);
      return;
    }
    if (perDsData.length > 1) {
      setOpen(!open);
      return;
    }
  };

  const renderLocations = (item) => {
    const allLocations = (item?.countries || []).flatMap(
      (countryGroup) => countryGroup?.locations || [],
    );
    const validLocations = processCountryData(allLocations);
    if (validLocations.length === 0) {
      return <div className="location-flag-cell">-</div>;
    }
    return renderCountryNamesWithPopover(
      validLocations,
      3,
      "location-flag-cell",
    );
  };

  const renderPieChart = (item) => {
    if (selectedCategory === "roles" || selectedCategory === "locations") {
      const closed = item.openCloseAnomaliesCount?.close || 0;
      const open = item.openCloseAnomaliesCount?.open || 0;
      if (closed === 0 && open === 0) {
        const valueArr = [
          { count: closed, name: "No Anomalies", color: "#4CAF50" },
        ];
        return renderDonutChart(valueArr, true);
      } else {
        const valueArr = [
          { count: closed, name: "Closed Anomalies", color: "#4CAF50" },
          { count: open, name: "Open Anomalies", color: "#FB0000" },
        ];
        return renderDonutChart(valueArr, true);
      }
    } else {
      const closed = item.totalAnomaliesCount - item.openAnomaliesCount;
      const open = item.openAnomaliesCount;
      if (closed === 0 && open === 0) {
        const valueArr = [
          { count: closed, name: "No Anomalies", color: "#4CAF50" },
        ];
        return renderDonutChart(valueArr, true);
      } else {
        const valueArr = [
          { count: closed, name: "Closed Anomalies", color: "#4CAF50" },
          { count: open, name: "Open Anomalies", color: "#FB0000" },
        ];
        return renderDonutChart(valueArr, true);
      }
    }
  };

  const renderAssetChart = (item) => {
    // Datasource-level row: single source, just show the count.
    if (item?.dataSourceId) {
      const val = formatNumber(item.dataAssetsCount ?? 0);
      return (
        <Chip
          size="medium"
          label={val}
          color="primary"
          className="user-chip numberValues"
        />
      );
    }

    const slices =
      item?.assetSourceSlices && item.assetSourceSlices.length > 0
        ? item.assetSourceSlices
        : [
            {
              count: item?.dataAssetsCount || 0,
              name: "Data Assets",
              color: "#4F6BD7",
            },
          ];

    return renderDonutChart(slices, true);
  };

  const renderAnomalies = (val, extraClass, col, item) => {
    if (col.id === "anomalyAccumulated") {
      return renderPieChart(item);
    }
    if (col.id === "uniqueDataAssetsAccumulated") {
      const slices = (item?.assetSourceSlices || []).map((slice) => {
        const match = slice.name?.match(/^(.*?)\s*\(([A-Z_]+)\)$/);

        return {
          count: slice.count,
          dataSourceName: match?.[1]?.trim() || slice.name,
          dataSourceType: match?.[2] || "",
        };
      });

      return renderPolarChart(slices);
    }

    if (col.chartType === "map") {
      return renderLocations(item);
    }

    return (
      <Chip
        size="medium"
        label={val}
        color="primary"
        className={`user-chip numberValues ${extraClass}`}
      />
    );
  };

  const renderChildRowCells = (item) => {
    const cells = [];

    cells.push(
      <TableCell
        key="datasource"
        align="left"
        style={{ width: columns[0]?.width, ...columns[0]?.customStyles }}
      >
        <div className="data-guard-accordion-type first-col-sub-accord">
          <img
            className="ds-icon"
            src={dataguard_icons[item.dataSourceType]}
            alt={item.dataSourceType}
          />
          <div>{item.dataSourceName}</div>
        </div>
      </TableCell>,
    );

    for (let i = 1; i < columns.length; i++) {
      const column = columns[i];
      const activeCol = activeColumns.find((col) => col.id === column.id);

      if (activeCol) {
        if (
          activeCol.key === "severity" &&
          selectedCategory === "behaviormodels"
        ) {
          cells.push(
            <TableCell
              key={column.id}
              align="center"
              style={{ width: column.width, ...column.customStyles }}
              onClick={(e) => {
                e.stopPropagation();
                onCellClick(e, row, activeCol, item);
              }}
            >
              <SeverityChips
                high={item.highCount}
                medium={item.mediumCount}
                low={item.lowCount}
              />
            </TableCell>,
          );
        } else {
          let extraClass = "",
            val = 0;
          val =
            typeof item[activeCol.key] === "object"
              ? formatNumber(item[activeCol.key].count)
              : formatNumber(item[activeCol.key]);

          if (
            ["anomalyCount", "anomaliesCount", "totalAnomaliesCount"].includes(
              activeCol.key,
            )
          ) {
            extraClass = val === "0" || val === 0 ? "success" : "error";
          }

          cells.push(
            <TableCell
              key={column.id}
              align="center"
              style={{ width: column.width, ...column.customStyles }}
              onClick={(e) => {
                e.stopPropagation();
                onCellClick(e, row, activeCol, item);
              }}
            >
              {!activeCol.isdummy
                ? renderAnomalies(val, extraClass, activeCol, item)
                : "-"}
            </TableCell>,
          );
        }
      } else {
        cells.push(
          <TableCell
            key={column.id}
            style={{ width: column.width, ...column.customStyles }}
          ></TableCell>,
        );
      }
    }

    if (!ACTION_NOT_NEEDED.includes(selectedCategory)) {
      cells.push(<TableCell key="toggle" padding="checkbox"></TableCell>);
    }

    return cells;
  };

  const showParentExpandIcon = isLocationsCategory
    ? true
    : perDsData.length > 1;

  return (
    <>
      {/* Parent Row */}
      <TableRow
        className="clickable-table-row parent-row"
        onClick={handleRowClick}
      >
        {columns.map((column) => (
          <TableCell
            style={{
              ...column.customStyles,
              width: column.width,
            }}
            align={column.align || "center"}
            key={column.id}
          >
            <div
              className="content-holder"
              onClick={(e) => {
                e.stopPropagation();
                onCellClick(e, row, column);
              }}
            >
              {column.renderCell
                ? column.renderCell(row[column.id], isIncognito)
                : row[column.id]}
            </div>
          </TableCell>
        ))}

        {/* Toggle Expand */}
        {!ACTION_NOT_NEEDED.includes(selectedCategory) && (
          <TableCell padding="checkbox">
            {showParentExpandIcon && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(!open);
                }}
                style={{
                  background: "#e5e9ff",
                  borderRadius: 5,
                  color: "#535458",
                }}
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </TableCell>
        )}
      </TableRow>

      {/* Expanded content */}
      {open && isLocationsCategory && (
        <TableRow className="city-grid-row-container">
          <TableCell
            colSpan={
              columns.length +
              (!ACTION_NOT_NEEDED.includes(selectedCategory) ? 1 : 0)
            }
            style={{ padding: 0, border: "none" }}
          >
            <CityLevelGrid
              row={row}
              columns={columns}
              onCellClick={onCellClick}
              renderAnomalies={renderAnomalies}
            />
          </TableCell>
        </TableRow>
      )}

      {/* Child Rows for non-locations categories - unchanged accordion */}
      {open &&
        !isLocationsCategory &&
        childRows.length > 1 &&
        childRows.map((item, idx) => (
          <TableRow
            key={`child-${idx}`}
            className={`clickable-table-row child-row ${open ? "child-row-visible" : ""}`}
            onClick={(e) => handleRowClick(e, item.dataSourceId, item.mapData)}
          >
            {renderChildRowCells(item)}
          </TableRow>
        ))}
    </>
  );
};

export default ExpandableTableRow;